import type { AxiosError } from 'axios';
import { createMutation } from 'react-query-kit';

import { client } from '@/api/common';

export type CsvAttachment = {
  name: string;
  file: File;
};

export type PriorRenter = {
  firstName: string;
  lastName: string;
  email: string;
};

export type ImportValidationError = {
  row: number;
  message: string;
};

export type ImportCustomersData = {
  importedCount: number;
  skippedCount: number;
  priorRenters: PriorRenter[];
  errorReportEmailed: boolean;
  errorReportCsv: string | null;
  failedCount: number;
  validationErrors: ImportValidationError[];
};

export type ImportCustomersResponse = {
  data: ImportCustomersData;
};

type ImportCustomersErrorData = {
  imported_count?: number;
  skipped_count?: number;
  failed_count?: number;
  prior_renters?: Array<{
    first_name: string;
    last_name: string;
    email: string;
  }>;
  error_report_emailed?: boolean;
  error_report_csv?: string | null;
  validation_errors?: Array<{
    row?: number;
    row_number?: number;
    message?: string;
    errors?: string[] | Record<string, string>;
  }>;
};

export type ImportCustomersErrorBody = {
  errors?: { file?: string };
  data?: ImportCustomersErrorData;
};

const parseValidationErrors = (
  errors: ImportCustomersErrorData['validation_errors']
): ImportValidationError[] => {
  if (!errors?.length) {
    return [];
  }

  return errors.flatMap((entry) => {
    const row = entry.row ?? entry.row_number;

    if (!row) {
      return [];
    }

    if (entry.message) {
      return [{ row, message: entry.message }];
    }

    if (Array.isArray(entry.errors)) {
      return entry.errors.map((message) => ({ row, message }));
    }

    if (entry.errors && typeof entry.errors === 'object') {
      return Object.values(entry.errors).map((message) => ({ row, message }));
    }

    return [];
  });
};

export const normalizeImportCustomersData = (
  data: ImportCustomersData
): ImportCustomersData => {
  const validationErrors = data.validationErrors ?? [];
  const failedCount = data.failedCount ?? validationErrors.length;

  return {
    ...data,
    validationErrors,
    failedCount,
    errorReportCsv: data.errorReportCsv ?? null,
  };
};

export const parseImportCustomersErrorData = (
  data: ImportCustomersErrorData | undefined
): ImportCustomersData | null => {
  if (!data) {
    return null;
  }

  const validationErrors = parseValidationErrors(data.validation_errors);
  const failedCount = data.failed_count ?? validationErrors.length;

  return normalizeImportCustomersData({
    importedCount: data.imported_count ?? 0,
    skippedCount: data.skipped_count ?? 0,
    priorRenters: (data.prior_renters ?? []).map((renter) => ({
      firstName: renter.first_name,
      lastName: renter.last_name,
      email: renter.email,
    })),
    errorReportEmailed: data.error_report_emailed ?? false,
    errorReportCsv: data.error_report_csv ?? null,
    failedCount,
    validationErrors,
  });
};

type Variables = {
  file: CsvAttachment;
};

export const useImportCustomers = createMutation<
  ImportCustomersResponse,
  Variables,
  AxiosError<ImportCustomersErrorBody>
>({
  mutationFn: async (variables) => {
    const formData = new FormData();
    formData.append('file', variables.file.file);

    return client({
      url: 'v2/internal/partners/organizations/customers/import',
      method: 'POST',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then((response) => response.data);
  },
});
